import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IShelterProduct from '../../interfaces/IShelterProduct'
import {
  SearchRequest,
  ShelterProductSaveRequest,
  ShelterProductDeleteRequest
} from '../../services/api/interfaces/ApiRequest'
import {
  shelterProductList,
  shelterProductCreate,
  shelterProductUpdate,
  shelterProductDelete
} from '../../services/api'

interface ShelterProductContextState extends BaseContextState {
  products: Array<IShelterProduct> | null,
  product: IShelterProduct | null,
}

interface ShelterProductContextData extends ShelterProductContextState {
  resetErrors: Function,
  requestShelterProductList: (args: SearchRequest) => void,
  requestShelterProductFind: (args: SearchRequest) => void,
  requestShelterProductCreate: (args: ShelterProductSaveRequest) => Promise<any>,
  requestShelterProductUpdate: (args: ShelterProductSaveRequest) => Promise<any>,
  requestShelterProductDelete: (args: ShelterProductDeleteRequest) => Promise<any>
}

// Create context
const { Provider, Consumer } = createContext({} as ShelterProductContextData)

// Export consumer
export const ShelterProductConsumer = Consumer

export class ShelterProductProvider extends BaseContextProvider {
  state: ShelterProductContextState = {
    products: null,
    product: null
  }

  lastListPayload: SearchRequest | null = null

  /**
   * Make api request for shelter product list
   * 
   * @param {SearchRequest} payload 
   */
  requestShelterProductList = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null
    }, async () => {
      this.lastListPayload = payload
      const response = await shelterProductList(payload)

      // Process our response
      this.processResponse(response, { 
        paginate: {
          destination: 'products'
        },
      })
    })
  }

  /**
   * Make api request for shelter product finder
   * 
   * @param {SearchRequest} payload 
   */
  requestShelterProductFind = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null,
      product: null
    }, async () => {
      payload.options = {
        ...payload.options,
        _single: true
      }
      const response = await shelterProductList(payload)

      // Process our response
      this.processResponse(response, { 
        mappings: {
          destination: 'product'
        }
      })
    })
  }

  /**
   * Make api request for shelter product create
   * 
   * @param {ShelterProductSaveRequest} payload 
   */
  requestShelterProductCreate = (payload: ShelterProductSaveRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        product: null
      }, async () => {
        let response = await shelterProductCreate(payload)
        this.processResponse(response, {
          keepFetching: true
        })

        if (this.state.success) {
          await this.requestShelterProductFind({
            search: {
              id: response.data.data?.organization_product.id,
              'product_branch': {
                with: {
                  deleted: false,
                  branch: {
                    with: true
                  }
                }
              },
              'product_specie': {
                with: {
                  deleted: false,
                  specie: {
                    with: true
                  }
                }
              }
            },
            options: {
              _force_ids: true
            }
          })
          resolve(true)
        } else {
          this.setState({
            fetching: false
          }, () => {
            reject(this.state.error)
          })
        }
      })
    })
  }

  /**
   * Make api request for shelter product update
   * 
   * @param {ShelterProductSaveRequest} payload 
   */
  requestShelterProductUpdate = (payload: ShelterProductSaveRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await shelterProductUpdate(payload)
        this.processResponse(response, {
          mappings: [
            { origin: 'organization_product', destination: 'product' }
          ]
        })
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for shelter product delete
   * 
   * @param {ShelterProductDeleteRequest} payload 
   */
  requestShelterProductDelete = (payload: ShelterProductDeleteRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await shelterProductDelete(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          this.requestShelterProductList(this.lastListPayload || {})
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestShelterProductList: this.requestShelterProductList,
          requestShelterProductFind: this.requestShelterProductFind,
          requestShelterProductCreate: this.requestShelterProductCreate,
          requestShelterProductUpdate: this.requestShelterProductUpdate,
          requestShelterProductDelete: this.requestShelterProductDelete
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
